import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAdminWithdrawals, approveWithdrawal, rejectWithdrawal, settleWithdrawal,
} from '../../api';
import {
  PageHeader, Table, Pagination, EmptyState, TableSkeleton, StatusBadge, AstrologerCell,
} from '../../components/ui';
import Modal from '../../components/ui/Modal';
import OverviewBar from '../../components/ui/OverviewBar';
import ExportButton from '../../components/ui/ExportButton';
import { formatCurrency, formatDateTime } from '../../utils/format';
import { CreditCard, Check, X, Landmark } from 'lucide-react';
import toast from 'react-hot-toast';

const LIMIT = 20;
const STATUSES = ['', 'pending', 'approved', 'rejected', 'settled'];

export default function WithdrawalsPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('pending');
  const [selected, setSelected] = useState<any | null>(null);
  const [action, setAction] = useState<'approve' | 'reject' | 'settle' | null>(null);
  const [adminNote, setAdminNote] = useState('');
  const [settlementReference, setSettlementReference] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-withdrawals', page, status],
    queryFn: () =>
      getAdminWithdrawals({ page, limit: LIMIT, status: status || undefined }).then((r) => r.data.data),
    placeholderData: (prev) => prev,
  });

  const rows = data?.data || [];
  const pagination = data?.pagination;

  const mutate = useMutation({
    mutationFn: async () => {
      if (!selected || !action) return;
      if (action === 'approve') return approveWithdrawal(selected._id, { adminNote });
      if (action === 'reject') return rejectWithdrawal(selected._id, { adminNote });
      return settleWithdrawal(selected._id, { settlementReference, adminNote });
    },
    onSuccess: () => {
      toast.success(`Withdrawal ${action}d`);
      qc.invalidateQueries({ queryKey: ['admin-withdrawals'] });
      setSelected(null);
      setAction(null);
      setAdminNote('');
      setSettlementReference('');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Action failed'),
  });

  const open = (row: any, a: 'approve' | 'reject' | 'settle') => {
    setSelected(row);
    setAction(a);
    setAdminNote('');
    setSettlementReference('');
  };

  const EXPORT_COLS = [
    { key: '_id', label: 'ID' },
    { key: 'amount', label: 'Amount (paise)' },
    { key: 'status', label: 'Status' },
    { key: 'settlementReference', label: 'Settlement ref' },
    { key: 'createdAt', label: 'Requested' },
  ];

  return (
    <div className="p-6">
      <PageHeader title="Withdrawals" subtitle="Approve, reject and settle astrologer withdrawal requests">
        <ExportButton data={rows} filename="withdrawals" columns={EXPORT_COLS} />
      </PageHeader>

      <OverviewBar
        items={[
          { label: 'In queue', value: pagination?.total ?? '—', icon: <CreditCard size={14} /> },
          { label: 'Page total', value: formatCurrency(rows.reduce((s: number, r: any) => s + (r.amount || 0), 0)), color: 'text-orange-600' },
        ]}
        loading={isLoading}
      />

      <div className="card p-4 mb-5 flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button
            key={s || 'all'}
            onClick={() => { setStatus(s); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg border text-xs font-medium capitalize ${status === s ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300 text-gray-600'}`}
          >
            {s || 'All'}
          </button>
        ))}
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-5"><TableSkeleton rows={8} cols={6} /></div>
        ) : rows.length === 0 ? (
          <EmptyState message="No withdrawal requests" icon={<CreditCard size={32} />} />
        ) : (
          <Table>
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="table-th">Astrologer</th>
                <th className="table-th">Amount</th>
                <th className="table-th">Bank / UPI</th>
                <th className="table-th">Status</th>
                <th className="table-th">Requested</th>
                <th className="table-th"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((w: any) => (
                <tr key={w._id} className="table-row">
                  <td className="table-td"><AstrologerCell entity={w.astrologerId} /></td>
                  <td className="table-td font-semibold">{formatCurrency(w.amount || 0)}</td>
                  <td className="table-td text-xs">
                    {w.bankSnapshot?.upiId || w.bankSnapshot?.accountNumber || '—'}
                    {w.bankSnapshot?.ifsc ? <span className="text-gray-400 block">{w.bankSnapshot.ifsc}</span> : null}
                  </td>
                  <td className="table-td"><StatusBadge status={w.status} /></td>
                  <td className="table-td text-xs">{formatDateTime(w.createdAt)}</td>
                  <td className="table-td">
                    <div className="flex gap-1 justify-end">
                      {w.status === 'pending' && (
                        <>
                          <button className="btn-secondary btn-sm text-green-700" onClick={() => open(w, 'approve')}><Check size={12} /></button>
                          <button className="btn-secondary btn-sm text-red-600" onClick={() => open(w, 'reject')}><X size={12} /></button>
                        </>
                      )}
                      {w.status === 'approved' && (
                        <button className="btn-primary btn-sm" onClick={() => open(w, 'settle')}><Landmark size={12} /> Settle</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {pagination?.pages > 1 && (
          <div className="p-4 border-t">
            <Pagination page={page} pages={pagination.pages} total={pagination.total} limit={LIMIT} onPageChange={setPage} />
          </div>
        )}
      </div>

      <Modal
        open={!!selected && !!action}
        onClose={() => { setSelected(null); setAction(null); }}
        title={action ? `${action.charAt(0).toUpperCase()}${action.slice(1)} withdrawal` : 'Withdrawal'}
      >
        {selected && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Amount <strong>{formatCurrency(selected.amount || 0)}</strong>
            </p>
            {action === 'settle' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Settlement reference</label>
                <input className="input" value={settlementReference} onChange={(e) => setSettlementReference(e.target.value)} placeholder="UTR / NEFT ref" />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Admin note</label>
              <textarea className="input" rows={3} value={adminNote} onChange={(e) => setAdminNote(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => { setSelected(null); setAction(null); }}>Cancel</button>
              <button
                className="btn-primary"
                disabled={mutate.isPending || (action === 'settle' && !settlementReference.trim())}
                onClick={() => mutate.mutate()}
              >
                Confirm
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
